/**
 * @since 2023-04-13
 * @author Francesco Parrella
 * @maintainer Francesco Parrella
 * @copyright AlgoTraders, All rights reserved
 */

import { authorizedFetch } from "./request";
import { createExcelDownload } from "./utils";

const acceptTermsAndConditions = async (email) => {
  try {
    const response = await authorizedFetch(
      `/strategy/acceptTermsAndConditions/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    await response.json();
  } catch (error) {
    console.error(error);
  }
};

const cancelSubscription = async (email) => {
  try {
    const response = await authorizedFetch(
      `/strategy/cancelSubscription/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    return await response.json();
  } catch (error) {
    console.error(error);
  }
};

const changePassword = async (currentPassword, newPassword) => {
  try {
    const response = await authorizedFetch(`/strategy/changePassword/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    const data = await response.json();
    return {
      success: response.ok && Boolean(data && data.success),
      error: data && data.error,
    };
  } catch (error) {
    console.error(error);
    return { success: false, error: "Could not change the password." };
  }
};

const downloadDailyReturns = async (
  email,
  moduleType,
  moduleId,
  moduleName
) => {
  try {
    const response = await authorizedFetch(
      `/strategy/downloadDailyReturns/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, moduleType, moduleId }),
      }
    );
    if (response.ok) {
      await createExcelDownload(response, moduleName + ".DailyReturns.xlsx");
    } else {
      console.error("Cannot download daily returns.");
    }
  } catch (error) {
    console.error(error);
  }
};

const downloadMonthlyReturns = async (
  email,
  moduleType,
  moduleId,
  moduleName
) => {
  try {
    const response = await authorizedFetch(
      `/strategy/downloadMonthlyReturns/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, moduleType, moduleId }),
      }
    );
    if (response.ok) {
      await createExcelDownload(response, moduleName + ".MonthlyReturns.xlsx");
    } else {
      console.error("Cannot download monthly returns.");
    }
  } catch (error) {
    console.error(error);
  }
};

const downloadTrades = async (email, moduleType, moduleId, moduleName) => {
  try {
    const response = await authorizedFetch(
      `/strategy/downloadTrades/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, moduleType, moduleId }),
      }
    );
    if (response.ok) {
      await createExcelDownload(response, moduleName + ".Trades.xlsx");
    } else {
      console.error("Cannot download trades.");
    }
  } catch (error) {
    console.error(error);
  }
};

const getSubscriptionLinks = async (email) => {
  try {
    const response = await authorizedFetch(
      `/strategy/getSubscriptionLinks/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    return await response.json();
  } catch (error) {
    console.error(error);
  }
};

const getTermsAndConditions = async () => {
  try {
    const response = await authorizedFetch(
      `/strategy/getTermsAndConditions/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    );
    return await response.json();
  } catch (error) {
    console.error(error);
  }
};

const initializeUser = async (email) => {
  try {
    const response = await authorizedFetch(
      `/strategy/initializeUser/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    await response.json();
  } catch (error) {
    console.error(error);
  }
};

const loadConfigurationData = async (email) => {
  try {
    const response = await authorizedFetch(
      `/strategy/getConfigurationData/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    return await response.json();
  } catch (error) {
    console.error(error);
  }
};

const loadMenuItems = async (email) => {
  try {
    const response = await authorizedFetch(`/strategy/getMenuItems/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
    return await response.json();
  } catch (error) {
    console.error(error);
  }
};

const notifyUserLogin = async (email) => {
  try {
    const response = await authorizedFetch(
      `/strategy/notifyUserLogin/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      }
    );
    await response.json();
  } catch (error) {
    console.error(error);
  }
};

const parseReturnsFile = async (file) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    // No Content-Type header: the browser sets multipart/form-data with its boundary
    const response = await authorizedFetch(`/strategy/parseReturnsFile/`, {
      method: "POST",
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) {
      // 400 (unsupported format) and 413 (too large) carry { success: false, error }
      return { error: (data && data.error) || "Could not load the file." };
    }
    return data;
  } catch (error) {
    console.error(error);
  }
};

const saveProfileSettings = async (email, dictProfileSettings) => {
  try {
    const response = await authorizedFetch(
      `/strategy/saveProfileSettings/`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, dictProfileSettings }),
      }
    );
    return await response.json();
  } catch (error) {
    console.error(error);
  }
};

const sendFeedback = async (email, category, feedback) => {
  try {
    const response = await authorizedFetch(`/strategy/sendFeedback/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, category, feedback }),
    });
    return await response.json();
  } catch (error) {
    console.error(error);
  }
};

export {
  acceptTermsAndConditions,
  cancelSubscription,
  changePassword,
  downloadDailyReturns,
  downloadMonthlyReturns,
  downloadTrades,
  getSubscriptionLinks,
  getTermsAndConditions,
  initializeUser,
  loadConfigurationData,
  loadMenuItems,
  notifyUserLogin,
  parseReturnsFile,
  saveProfileSettings,
  sendFeedback,
};
